import { useEffect, useState } from "react";
import {
  Paper,
  Stack,
  Select,
  TextInput,
  Button,
  Title,
  Text,
  Alert,
  Group,
  Loader,
} from "@mantine/core";
import { Link } from "wouter";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { getPanels, createPanelSession } from "./panelApi";

export default function PanelSessionConfig() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelId, setPanelId] = useState(null);
  const [title, setTitle] = useState("");
  const [starting, setStarting] = useState(false);

  const setSelected = useStore((state) => state.setSelectedPanelSession);
  const setTimestamp = useStore((state) => state.setPanelSessionListTimestamp);

  useEffect(() => {
    getPanels()
      .then(setPanels)
      .finally(() => setLoading(false));
  }, []);

  const start = async () => {
    if (!panelId) return;
    setStarting(true);
    try {
      const session = await createPanelSession({ panelId, title: title || null });
      setTimestamp(Date.now());
      setSelected(session);
    } catch (err) {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <Loader />;

  if (panels.length === 0) {
    return (
      <Alert title="No panels yet" color="blue">
        <Stack align="flex-start">
          <Text size="sm">
            A panel is a group of models with different personas. Create one to
            start deliberating.
          </Text>
          <Button component={Link} to="/panels/manage/new" variant="light">
            Create a panel
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack>
        <Title order={4}>New conversation</Title>
        <Select
          label="Panel"
          placeholder="Pick a panel"
          data={panels.map((p) => ({
            value: p.id,
            label: `${p.name} · ${p.panelists?.length ?? 0} seats`,
          }))}
          value={panelId}
          onChange={setPanelId}
          searchable
        />
        <TextInput
          label="Conversation title (optional)"
          placeholder="Defaults to the panel name"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Group justify="space-between">
          <Button component={Link} to="/panels/manage" variant="subtle" size="xs">
            Manage panels
          </Button>
          <Button onClick={start} loading={starting} disabled={!panelId}>
            Start conversation
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
